/**
 * Sell-draft domain — pure functions for the web creation-flow drafts.
 *
 *   ListingData (form state)  ──serializeListingDraft──▶  SellDraftPayload (jsonb)
 *   SellDraftPayload (jsonb)  ──parseListingDraft─────▶  ListingData | unreadable
 *
 * Everything here is side-effect free (no supabase, no fetch, no Date.now) so
 * the quit-guard gate, the round-trip, and the prune diff are unit-testable
 * without React or Next.js — see tests/unit/sell-draft-*.test.ts.
 *
 * Photo lifecycle contract (decided in the drafts plan, 2026-08-05):
 *  - Draft photos are uploaded under the SAME `listings/…` namespace as
 *    publish (`StoragePath.listing`), so publishing a resumed draft reuses the
 *    paths verbatim in `image_urls` — zero file moves. The review steps
 *    already accept `{ file: null, preview: <bucket path> }` via
 *    `isPersistedImageRef`.
 *  - Rehydrated photos therefore come back as `{ file: null, preview: path }`.
 *
 * @see packages/contract/src/sell-draft.ts — payload + wire schemas
 * @see apps/mobile/lib/sell-draft-meaningful.ts — mobile sibling gate (local
 *      AsyncStorage drafts). Ported here with one fix: web's pristine form has
 *      `listing_format: undefined`, so the mobile `!== "fixed_price"` check
 *      must be undefined-safe or an untouched form counts as meaningful.
 */

import {
  SellDraftPayloadSchema,
  rawImageRefValue,
  type ListingCategory,
  type RawImageRef,
  type SellDraftPayload,
  type SellDraftPhoto,
} from "@kraahl/contract"

import type { ListingData } from "@/types/listing"

/** Hard cap enforced by the save route (409 DRAFT_LIMIT_REACHED). */
export const DRAFTS_PER_USER_CAP = 20

/**
 * The pristine creation-flow form — single source shared by the container's
 * initial state, its reset-on-close, and `parseListingDraft`'s required-field
 * fallbacks. Returns a fresh object (arrays are mutated by steps).
 */
export function emptyListingData(): ListingData {
  return {
    photos: [],
    title: "",
    description: "",
    condition: "",
    grade: "",
    shipping: { methods: [], customOptions: [] },
    quantity: 1,
  }
}

/**
 * Form state → persistable payload. Strips what cannot survive JSON or
 * another device: in-memory `File`s (`photos`/`editedPhotos` — the uploaded
 * `photos` list is passed in separately), the photo-editor state (blob:/data:
 * URLs), and identity/timestamps that belong to a published listing.
 */
export function serializeListingDraft(
  data: ListingData,
  photos: SellDraftPhoto[],
  step: number,
  now: Date,
): SellDraftPayload {
  const {
    photos: _photos,
    editedPhotos: _editedPhotos,
    photoEditorState: _photoEditorState,
    id: _id,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...form
  } = data

  return {
    version: 1,
    savedAt: now.toISOString(),
    step,
    listingCategory: data.listingCategory,
    photos,
    form: form as Record<string, unknown>,
  }
}

export type SellDraftParseResult =
  | { kind: "ok"; data: ListingData; step: number; imagePaths: RawImageRef[] }
  | { kind: "unreadable" }

/**
 * Payload (as read from `listing_drafts.payload`) → rehydrated form state.
 * Unknown version or structural mismatch → `unreadable` — the caller keeps
 * the draft listed (delete-only) instead of silently dropping user data.
 *
 * Required `ListingData` fields are defaulted from the pristine form so a
 * hand-edited/partial payload can never produce `title: undefined`; `photos`
 * is set AFTER the form spread so a stray `photos` key inside `form` can
 * never override the hydrated `{ file: null }` list.
 */
export function parseListingDraft(payload: unknown): SellDraftParseResult {
  const parsed = SellDraftPayloadSchema.safeParse(payload)
  if (!parsed.success) return { kind: "unreadable" }

  const { form, photos, step, listingCategory } = parsed.data

  const data: ListingData = {
    ...emptyListingData(),
    ...(form as Partial<ListingData>),
    photos: photos.map((p, i) => ({
      file: null,
      preview: p.path,
      photoId: p.photoId || `draft_${i}`,
    })),
    listingCategory:
      (form.listingCategory as ListingData["listingCategory"]) ?? listingCategory,
  }
  // Editor state never round-trips (blob:/data: URLs are device-local); a
  // legacy payload that carried them would rehydrate dead previews.
  delete data.editedPhotos
  delete data.photoEditorState

  return {
    kind: "ok",
    data,
    step,
    imagePaths: photos.map((p) => p.path),
  }
}

/**
 * "Is there anything worth saving?" — gates BOTH the quit dialog (pristine
 * form closes silently) and the beforeunload warning. Port of the mobile
 * gate; see header for the `listing_format` undefined-safety fix. `currency`
 * (auto-patched from the profile) and `quantity` (defaults to 1) are
 * deliberately NOT meaningful.
 */
export function isListingDraftMeaningful(data: ListingData): boolean {
  if (data.photos.length > 0) return true
  if ((data.editedPhotos?.length ?? 0) > 0) return true
  if (data.title.trim().length > 0) return true
  if (data.description.trim().length > 0) return true
  if (data.condition.trim().length > 0) return true
  if (data.grade.trim().length > 0) return true
  if (data.shipping.methods.length > 0) return true
  if (data.shipping.customOptions.length > 0) return true
  if (data.brand_id || data.theme_id || data.product_type_id) return true
  if (
    data.brand_custom_text?.trim() ||
    data.theme_custom_text?.trim() ||
    data.product_type_custom_text?.trim()
  ) {
    return true
  }
  if (data.price != null && data.price > 0) return true
  if (data.auction_start_price != null && data.auction_start_price > 0) return true
  if (data.scheduled_publish_at) return true
  if (data.listing_type === "preorder") return true
  if (data.listing_format != null && data.listing_format !== "fixed_price") return true
  if (data.cardDraft?.cardName?.trim()) return true
  if (data.comicDraft != null) return true
  if (data.listingCategory != null && CATEGORY_IMPLIES_MEANINGFUL[data.listingCategory]) {
    return true
  }
  return false
}

/**
 * Picking a specialised category means the seller progressed past the
 * category fork into that flow — worth saving. `lego` is the default flow's
 * value, reachable with zero intent. Record keyed by the canonical tuple:
 * a new category fails to compile until it takes a position here
 * (listing-category extension protocol).
 */
const CATEGORY_IMPLIES_MEANINGFUL: Record<ListingCategory, boolean> = {
  lego: false,
  trading_card: true,
  comic: true,
}

/**
 * Deterministic content signature for dirty-tracking: the quit guard compares
 * the current form's signature against the baseline captured at open (empty
 * form or hydrated draft). Field set mirrors what serialization persists;
 * `currency` is excluded for the same reason as in the meaningful gate.
 */
export function listingDraftSignature(data: ListingData): string {
  const effectivePhotos =
    data.editedPhotos && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos
  return JSON.stringify([
    data.title,
    data.description,
    data.price ?? null,
    data.condition,
    data.grade,
    data.quantity,
    data.listingCategory ?? null,
    data.listing_format ?? null,
    data.auto_decline_below ?? null,
    data.auction_start_price ?? null,
    data.auction_reserve_price ?? null,
    data.auction_end_at ?? null,
    data.listing_type ?? null,
    data.preorder_deadline ?? null,
    data.estimated_delivery ?? null,
    data.min_orders ?? null,
    data.max_orders ?? null,
    data.scheduled_publish_at ?? null,
    data.brand_id ?? null,
    data.theme_id ?? null,
    data.product_type_id ?? null,
    data.brand_custom_text ?? null,
    data.theme_custom_text ?? null,
    data.product_type_custom_text ?? null,
    data.shipping.methods,
    data.shipping.customOptions,
    data.shippingMode ?? null,
    data.parcel ?? null,
    data.ship_from_address_id ?? null,
    data.cardDraft ?? null,
    data.comicDraft ?? null,
    effectivePhotos.map((p) => `${p.photoId}:${rawImageRefValue(p.preview)}`),
  ])
}

/**
 * Paths present in `previous` but absent from `next` — the save route's prune
 * set when re-saving a resumed draft after the seller removed photos.
 * Order-stable, deduplicated.
 */
export function diffRemovedImagePaths(
  previous: readonly RawImageRef[],
  next: readonly RawImageRef[],
): RawImageRef[] {
  const keep = new Set(next.map((p) => rawImageRefValue(p)))
  const seen = new Set<string>()
  const removed: RawImageRef[] = []
  for (const path of previous) {
    const key = rawImageRefValue(path)
    if (keep.has(key) || seen.has(key)) continue
    seen.add(key)
    removed.push(path)
  }
  return removed
}
